import * as React from "react";
import {
  Badge,
  Button,
  Dialog,
  FilterSearch,
  FilterSelect,
  FiltersToolbar,
  FormField,
  Input,
  Select,
  Textarea,
  formatDateTimeVn,
  useToast,
} from "@chotca/ui";
import { PLAN_CODES, type PlanGiftCode } from "@chotca/config";
import {
  useAdminGiftCodes,
  useAdminOrgs,
  useCreateGiftCodes,
  useRevokeGiftCode,
} from "@chotca/api-client";
import { Ban, Copy } from "lucide-react";
import { useTableFilters } from "@/lib/use-table-filters";
import {
  DataTable,
  usePagination,
  type Column,
  type RowAction,
} from "@/components/data-table";

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Chưa dùng" },
  { value: "REDEEMED", label: "Đã kích hoạt" },
  { value: "REVOKED", label: "Đã thu hồi" },
];

// Common gift durations, so admin doesn't type raw days for the usual cases.
const DURATION_OPTIONS = [
  { value: "30", label: "1 tháng (30 ngày)" },
  { value: "90", label: "3 tháng (90 ngày)" },
  { value: "180", label: "6 tháng (180 ngày)" },
  { value: "365", label: "1 năm (365 ngày)" },
];

function statusBadge(status: PlanGiftCode["status"]) {
  if (status === "REDEEMED") return <Badge tone="lime">Đã kích hoạt</Badge>;
  if (status === "REVOKED") return <Badge tone="smoke">Đã thu hồi</Badge>;
  return <Badge tone="mist">Chưa dùng</Badge>;
}

export function GiftCodesTab() {
  const { toast } = useToast();
  const filters = useTableFilters({
    search: { debounce: 300 },
    status: {},
  });
  const { page, pageSize, setPage, setPageSize } = usePagination();
  const [creating, setCreating] = React.useState(false);

  const listQuery = {
    search: filters.applied.search,
    status: filters.applied.status as PlanGiftCode["status"] | undefined,
    page,
    limit: pageSize,
  };
  const { data, isLoading, isFetching } = useAdminGiftCodes(listQuery);
  const revoke = useRevokeGiftCode();

  const list = (data?.items ?? []) as PlanGiftCode[];

  async function copyCode(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      toast({ tone: "success", title: `Đã sao chép ${code}` });
    } catch {
      toast({ tone: "error", title: "Không sao chép được" });
    }
  }

  const columns: Column<PlanGiftCode>[] = [
    {
      key: "code",
      header: "Mã",
      cell: (g) => (
        <button
          type="button"
          onClick={() => copyCode(g.code)}
          className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-ink hover:underline"
          title="Sao chép mã"
        >
          {g.code}
          <Copy size={12} className="text-ash" />
        </button>
      ),
    },
    {
      key: "org",
      header: "Doanh nghiệp",
      cell: (g) => (
        <span className="line-clamp-1 max-w-52 text-sm">{g.orgName}</span>
      ),
    },
    {
      key: "plan",
      header: "Gói",
      cell: (g) => (
        <span className="rounded-full bg-mist px-2 py-0.5 text-[11px] font-bold uppercase">
          {g.planCode}
        </span>
      ),
    },
    {
      key: "duration",
      header: "Thời hạn",
      align: "right",
      cell: (g) => <span className="text-xs">{g.durationDays} ngày</span>,
    },
    {
      key: "status",
      header: "Trạng thái",
      cell: (g) => statusBadge(g.status),
    },
    {
      key: "redeemBy",
      header: "Hạn dùng",
      cell: (g) => (
        <span className="text-xs text-ash">
          {g.redeemBy ? formatDateTimeVn(g.redeemBy) : "—"}
        </span>
      ),
    },
    {
      key: "redeemedAt",
      header: "Kích hoạt lúc",
      cell: (g) => (
        <span className="text-xs text-ash">
          {g.redeemedAt ? formatDateTimeVn(g.redeemedAt) : "—"}
        </span>
      ),
    },
    {
      key: "note",
      header: "Ghi chú",
      cell: (g) => (
        <span className="line-clamp-1 max-w-48 text-xs text-ash">
          {g.note ?? "—"}
        </span>
      ),
    },
  ];

  const rowActions: RowAction<PlanGiftCode>[] = [
    {
      key: "revoke",
      label: "Thu hồi",
      icon: Ban,
      destructive: true,
      // Only unredeemed codes can be revoked; hide the action otherwise.
      show: (g) => g.status === "ACTIVE",
      onClick: (g) => {
        revoke.mutate(g.id, {
          onSuccess: () => toast({ tone: "success", title: "Đã thu hồi mã" }),
          onError: () => toast({ tone: "error", title: "Thu hồi thất bại" }),
        });
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)}>+ Tạo mã quà tặng</Button>
      </div>

      <FiltersToolbar
        hasActiveFilters={filters.hasAny}
        onClearAll={filters.clear}
      >
        <FilterSearch
          value={filters.values.search ?? ""}
          onChange={(v) => filters.set("search", v)}
          placeholder="Tìm mã hoặc tên doanh nghiệp..."
        />
        <FilterSelect
          value={filters.values.status ?? ""}
          onValueChange={(v) => filters.set("status", v)}
          placeholder="Mọi trạng thái"
          options={STATUS_OPTIONS}
        />
      </FiltersToolbar>

      <DataTable
        columns={columns}
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(g) => g.id}
        rowActions={rowActions}
        emptyText="Chưa có mã quà tặng nào khớp bộ lọc."
        page={page}
        pageSize={pageSize}
        total={data?.total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      <CreateGiftCodesDialog
        open={creating}
        onClose={() => setCreating(false)}
      />
    </div>
  );
}

// Mint one gift code for an org. On success shows the freshly minted code so
// the admin can copy it to hand out.
function CreateGiftCodesDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const create = useCreateGiftCodes();

  const [orgSearch, setOrgSearch] = React.useState("");
  const [orgId, setOrgId] = React.useState("");
  const [planCode, setPlanCode] = React.useState<string>("PRO");
  const [durationDays, setDurationDays] = React.useState("180");
  const [redeemBy, setRedeemBy] = React.useState("");
  const [note, setNote] = React.useState("");
  const [minted, setMinted] = React.useState<string | null>(null);

  // Debounce the org search so we don't hammer the list endpoint per keystroke.
  const [debouncedOrgSearch, setDebouncedOrgSearch] = React.useState("");
  React.useEffect(() => {
    const id = setTimeout(() => setDebouncedOrgSearch(orgSearch), 300);
    return () => clearTimeout(id);
  }, [orgSearch]);
  const orgs = useAdminOrgs(
    debouncedOrgSearch.trim().length >= 2
      ? { search: debouncedOrgSearch.trim(), limit: 10 }
      : {},
  );

  React.useEffect(() => {
    if (!open) {
      setOrgSearch("");
      setOrgId("");
      setPlanCode("PRO");
      setDurationDays("180");
      setRedeemBy("");
      setNote("");
      setMinted(null);
    }
  }, [open]);

  const orgOptions = (orgs.data?.items ?? []).map((o) => ({
    value: o.id,
    label: o.name,
  }));
  // Non-FREE plans only — you don't gift Miễn phí.
  const planOptions = PLAN_CODES.filter((c) => c !== "FREE").map((c) => ({
    value: c,
    label: c,
  }));

  async function submit() {
    if (!orgId) {
      toast({ tone: "error", title: "Chọn doanh nghiệp" });
      return;
    }
    try {
      const res = await create.mutateAsync({
        orgId,
        planCode: planCode as (typeof PLAN_CODES)[number],
        durationDays: Number(durationDays),
        // datetime-local yields a local wall time; send it as an ISO instant.
        redeemBy: redeemBy ? new Date(redeemBy).toISOString() : null,
        note: note.trim() || undefined,
      });
      setMinted(res.items[0]?.code ?? null);
      toast({ tone: "success", title: "Đã tạo mã quà tặng" });
    } catch {
      toast({ tone: "error", title: "Tạo mã thất bại" });
    }
  }

  async function copyCode() {
    if (!minted) return;
    try {
      await navigator.clipboard.writeText(minted);
      toast({ tone: "success", title: `Đã sao chép ${minted}` });
    } catch {
      toast({ tone: "error", title: "Không sao chép được" });
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Tạo mã quà tặng"
      footer={
        minted ? (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Đóng
            </Button>
            <Button onClick={copyCode}>Sao chép mã</Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Huỷ
            </Button>
            <Button
              onClick={submit}
              loading={create.isPending}
              disabled={!orgId}
            >
              Tạo mã
            </Button>
          </div>
        )
      }
    >
      {minted ? (
        <div className="space-y-3">
          <p className="text-sm text-ash">
            Đã tạo mã. Sao chép và gửi cho khách — mã chỉ dùng được một lần.
          </p>
          <div className="rounded-md border border-line bg-mist p-3">
            <div className="font-mono text-base font-semibold text-ink">
              {minted}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <FormField label="Doanh nghiệp">
            <Input
              value={orgSearch}
              onChange={(e) => {
                setOrgSearch(e.target.value);
                setOrgId("");
              }}
              placeholder="Gõ tên doanh nghiệp để tìm..."
            />
            {orgOptions.length > 0 && !orgId ? (
              <div className="mt-1 max-h-40 space-y-0.5 overflow-y-auto rounded-md border border-line bg-white p-1">
                {orgOptions.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => {
                      setOrgId(o.value);
                      setOrgSearch(o.label);
                    }}
                    className="block w-full truncate rounded px-2 py-1 text-left text-sm hover:bg-mist"
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            ) : null}
            {orgId ? (
              <p className="mt-1 text-body-xs text-lime-dark">
                ✓ Đã chọn: {orgSearch}
              </p>
            ) : null}
          </FormField>

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Gói tặng">
              <Select
                value={planCode}
                onValueChange={setPlanCode}
                options={planOptions}
              />
            </FormField>
            <FormField label="Thời hạn">
              <Select
                value={durationDays}
                onValueChange={setDurationDays}
                options={DURATION_OPTIONS}
              />
            </FormField>
            <FormField label="Hạn dùng">
              <Input
                type="datetime-local"
                value={redeemBy}
                onChange={(e) => setRedeemBy(e.target.value)}
              />
            </FormField>
          </div>

          <FormField label="Ghi chú (nội bộ)">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="VD: tặng đối tác ABC, chiến dịch 12/2026"
              rows={2}
            />
          </FormField>
        </div>
      )}
    </Dialog>
  );
}
